// A BIBLIOTECA — ver e acompanhar histórias publicadas na plataforma.
//
// O pedido: "uma nova visão que permita mostrar e distribuir quadrinhos na plataforma como se
// fosse uma sucessão de material para a gente ver e poder acompanhar aquelas histórias, inclusive
// com estruturas de capítulos e categorias".
//
// Duas coisas que esta tela faz e a plataforma não tinha:
//
//   1. SÉRIE COM CAPÍTULOS. Quadrinhos que pertencem à mesma série aparecem juntos, em ordem de
//      capítulo, com o próximo destacado. É o "acompanhar a história" do pedido — sem isso, uma
//      série de seis capítulos é seis itens soltos numa grade.
//
//   2. CATEGORIA. Material didático, história narrativa e criação livre são prateleiras
//      diferentes, porque quem procura material de aula não quer o mesmo que quem quer ler.
//
// A tela é pública de propósito: distribuir é o verbo do pedido, e exigir login para ver o que a
// escola publicou esvaziaria a biblioteca.

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Nav from "../../components/nav";
import Firebase from "../../api/firebase.class";
import { meusVinculos } from "../../instituicao/api";
import {
  AJUDA_EXIBICAO,
  EXIBICOES,
  Exibicao,
  ROTULO_EXIBICAO,
  naExibicao,
} from "../../publicar/publicacao";
import { useGlobalState } from "../../global-state-context";
import { where } from "firebase/firestore";
import {
  Categoria,
  DocumentoHQ,
  ROTULO_CATEGORIA,
} from "../../api/documento.types";
import { estadoVazio } from "./estado-vazio";
import { paginasDaFicha, rotuloDePaginas } from "./contagem-de-paginas";

import "./biblioteca.scss";
import { ehResiduoDeTeste } from "../../api/residuo-de-teste";

/**
 * O FILTRO DA BIBLIOTECA, e ele ganhou uma opcao que nao e categoria.
 *
 * T2.73: "nao sei se em biblioteca os meus quadrinhos ficariam mais organizados, de colocar tipo
 * assim BIBLIOTECA DA INSTITUICAO (...) porque ai voce poderia ver TANTO A BIBLIOTECA ABERTA
 * QUANTO um desses filtrinhos ser tipo assim da sua instituicao especificamente."
 *
 * A resposta imediata da outra pessoa foi "perfeitamente, concordo" — fica decidido na hora.
 *
 * "minha-escola" e uma opcao ao LADO das categorias e nao no lugar delas, porque e isso que ele
 * pede: as duas bibliotecas existindo, com um filtro escolhendo. E o T2.79 fecha o argumento de
 * por que a aberta continua aberta: "o aluno em teoria poderia entrar independentemente. ELE SO
 * NAO VAI SER ASSOCIADO AQUELA BIBLIOTECA ESPECIFICA."
 */
type Filtro = "todas" | "minha-escola" | Categoria;

interface SerieAgrupada {
  id: string;
  titulo: string;
  categoria: Categoria;
  capitulos: DocumentoHQ[];
}

/**
 * Agrupa por série e ordena por capítulo.
 *
 * Quadrinho sem série é uma série de um capítulo só — assim a tela tem um caminho só de
 * renderização, em vez de dois blocos que se desencontram.
 */
function agruparEmSeries(docs: DocumentoHQ[]): SerieAgrupada[] {
  const porSerie = new Map<string, SerieAgrupada>();

  for (const doc of docs) {
    const serie = doc.publicacao?.serie;
    const id = serie?.id ?? `avulso:${doc.id}`;
    const existente = porSerie.get(id);
    if (existente) {
      existente.capitulos.push(doc);
    } else {
      porSerie.set(id, {
        id,
        titulo: serie?.titulo ?? doc.titulo,
        categoria: doc.publicacao?.categoria ?? "livre",
        capitulos: [doc],
      });
    }
  }

  // `Array.from` em vez de espalhar o iterador: o TypeScript deste projeto compila para ES5,
  // onde espalhar um iterador de Map exige `downlevelIteration`. Mudar a configuração do
  // compilador por causa de uma linha afetaria o projeto inteiro.
  const series: SerieAgrupada[] = Array.from(porSerie.values());

  series.forEach((s) =>
    s.capitulos.sort(
      (a, b) => (a.publicacao?.serie?.capitulo ?? 1) - (b.publicacao?.serie?.capitulo ?? 1)
    )
  );

  // Série com mais capítulos primeiro; depois, a atualizada mais recentemente.
  return series.sort((a: SerieAgrupada, b: SerieAgrupada) => {
    if (b.capitulos.length !== a.capitulos.length) return b.capitulos.length - a.capitulos.length;
    const ua = a.capitulos[a.capitulos.length - 1]?.atualizadoEm ?? "";
    const ub = b.capitulos[b.capitulos.length - 1]?.atualizadoEm ?? "";
    return ub.localeCompare(ua);
  });
}

function Biblioteca() {
  const navigate = useNavigate();
  const globalState = useGlobalState();
  const [docs, setDocs] = useState<DocumentoHQ[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [filtro, setFiltro] = useState<Filtro>("todas");

  /**
   * AS DUAS EXIBICOES DO T2.60, e este ponto estava BLOQUEADO pela duvida D2 desde a decomposicao.
   *
   * "temos duas opcoes de exibicao na biblioteca: um que vai fazer os NAO FINALIZADOS, mas que
   *  vao estar distribuidos, ou seja, VOCE PODE ABRIR ELE AMANHA E O BALAO DE FALA TER MUDADO,
   *  qualquer coisa ter mudado; mas voce tem um outro tipo de estrutura que e os FINALIZADOS, na
   *  qual aquela pessoa nao tem mais como efetivamente editar aquele quadrinho depois."
   *
   * A distincao dele NAO e "bom e ruim" — e ESTAVEL e MUTAVEL. Quem abre um em andamento pode
   * voltar amanha e achar outra coisa; quem abre um finalizado acha o mesmo. Os rotulos em
   * `publicacao.ts` dizem isso, e por isso sao "Prontos" e "Ainda mudando", e nao "completo" e
   * "incompleto".
   *
   * A D2 travava o ponto porque ela decide se "finalizado" tem saida. Decidida em 12/09 com a
   * autonomia que o operador deu: tem, e a saida e deletar e subir outro.
   */
  const [exibicao, setExibicao] = useState<Exibicao>("todos");

  /**
   * AS ESCOLAS DE QUE EU SOU MEMBRO ATIVO.
   *
   * Lista vazia para quem esta deslogado, e isso nao e caso de erro: a biblioteca e publica de
   * proposito ("distribuir e o verbo do pedido"), e quem chega sem conta simplesmente nao ve o
   * filtro de escola. Uma tela que exigisse login para MOSTRAR o filtro esvaziaria a prateleira
   * para o visitante, que e o oposto do que ela existe para fazer.
   */
  const [minhasEscolas, setMinhasEscolas] = useState<string[]>([]);

  useEffect(() => {
    const uid = globalState?.appUser?.Id();
    if (!uid) {
      setMinhasEscolas([]);
      return;
    }
    let vivo = true;
    meusVinculos(uid).then((vs) => {
      if (!vivo) return;
      // So vinculo ATIVO conta. Pendente e recusado nao dao acesso, e um filtro que mostrasse a
      // escola de quem ainda espera aprovacao prometeria o que a regra nega — a pessoa clicaria
      // e veria a ficha sem conseguir abrir a historia.
      setMinhasEscolas(vs.filter((v) => v.situacao === "ativo").map((v) => v.instituicaoId));
    });
    return () => {
      vivo = false;
    };
  }, [globalState]);
  const [busca, setBusca] = useState("");

  // Medido em produção em 25/09: esta aba dizia só "HQ-Lab", enquanto /parceiros já dizia
  // "Parceiros · HQ-Lab" e /roteirizar "Do texto ao quadrinho · HQ-Lab". Metade do T8.39 é
  // isto, e a outra metade são as telas que ainda faltam — a varredura é do TS-T8.14.2.
  useEffect(() => {
    document.title = "Biblioteca · HQ-Lab";
    return () => {
      document.title = "HQ-Lab";
    };
  }, []);

  useEffect(() => {
    // O FILTRO VAI NA CONSULTA, e não só no JavaScript depois.
    //
    // Esta linha era `undefined` e a filtragem acontecia só no `forEach` abaixo. Contra um
    // Firestore SEM regra para esta coleção, dava no mesmo: nada voltava de qualquer jeito.
    // Contra a regra publicada, a diferença é entre funcionar e não funcionar — o Firestore
    // avalia a regra contra a CONSULTA, e uma listagem sem `where` não consegue provar que só
    // pediu o que é público. Resultado: permission-denied para todo mundo, inclusive logado.
    //
    // Achado por validação independente, medindo a consulta EXATA desta tela contra o emulador,
    // ANTES de as regras irem para produção. Sem isso o operador teria publicado as regras e a
    // biblioteca continuaria vazia — com uma explicação pior, porque pareceria configurada.
    //
    // O `forEach` abaixo continua conferindo o mesmo campo, e não é redundância inútil: a regra é
    // a garantia, e a checagem na tela é o que impede um documento mal formado de aparecer se a
    // regra um dia for afrouxada.
    const cancelar = Firebase.onCollectionSnapshot(
      "biblioteca",
      (snap) => {
        const lista: DocumentoHQ[] = [];
        snap.forEach((d) => {
          const dado = d.data() as DocumentoHQ;
          // T30.9 · o que quem testa marcou como teste não aparece ao público.
          if (dado?.publicacao?.estado === "publicado" && !ehResiduoDeTeste(dado.titulo)) lista.push(dado);
        });
        setDocs(lista);
      },
      where("publicacao.estado", "==", "publicado"),
      200,
      // Sem este caminho a tela girava 8 segundos e so entao dizia que nao carregou, mesmo quando
      // a resposta ja tinha chegado como "negado" no primeiro segundo. Errar rapido e mais
      // gentil do que errar devagar.
      () => setErro("Não consegui carregar a biblioteca agora. Tente recarregar a página.")
    );
    return () => {
      try {
        cancelar?.();
      } catch (e) {
        // Firebase pode não estar configurado (a chave é opcional em desenvolvimento).
        console.warn("[biblioteca] não consegui encerrar a escuta", e);
      }
    };
  }, []);

  useEffect(() => {
    // Se em 8 segundos nada chegou, é mais honesto dizer do que girar para sempre.
    if (docs !== null) return;
    const t = setTimeout(() => {
      setErro("Não consegui carregar a biblioteca agora. Tente recarregar a página.");
    }, 8000);
    return () => clearTimeout(t);
  }, [docs]);

  const series = useMemo(() => {
    if (!docs) return [];
    const termo = busca.trim().toLowerCase();
    const filtrados = docs.filter((d) => {
      if (!naExibicao(d, exibicao)) return false;
      if (filtro === "minha-escola") {
        const escopo = d.publicacao?.instituicaoId;
        if (!escopo || !minhasEscolas.includes(escopo)) return false;
      } else if (filtro !== "todas" && d.publicacao?.categoria !== filtro) {
        return false;
      }
      if (!termo) return true;
      const alvo = [d.titulo, d.autor?.nome, d.autor?.turma, d.publicacao?.serie?.titulo]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return alvo.includes(termo);
    });
    return agruparEmSeries(filtrados);
  }, [docs, filtro, busca, minhasEscolas, exibicao]);

  const filtros: { id: Filtro; nome: string }[] = [
    { id: "todas", nome: "Todas" },
    // So aparece para quem TEM escola — um filtro que nao filtra nada e um botao que decepciona.
    ...(minhasEscolas.length > 0
      ? [{ id: "minha-escola" as Filtro, nome: "Da minha escola" }]
      : []),
    { id: "didatico", nome: ROTULO_CATEGORIA.didatico },
    { id: "narrativa", nome: ROTULO_CATEGORIA.narrativa },
    { id: "livre", nome: ROTULO_CATEGORIA.livre },
  ];

  return (
    <div className="biblioteca-container">
      <Nav />

      <header className="biblioteca-topo hq-trama">
        <div className="biblioteca-topo-conteudo">
          <div>
            <h1>Biblioteca</h1>
            <p>Histórias publicadas pelas turmas. Leia, acompanhe e continue de onde parou.</p>
          </div>
          <label className="biblioteca-busca">
            <span className="visualmente-oculto">Buscar história, autor ou turma</span>
            <input
              type="search"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar história, autor ou turma"
            />
          </label>
        </div>
      </header>

      <main className="biblioteca-conteudo">
        {/* AS DUAS EXIBICOES (T2.60) VEM ANTES DAS CATEGORIAS, e a ordem diz o que elas sao:
            categoria e PRATELEIRA (do que trata), exibicao e ESTADO (se ainda muda). Misturar as
            duas numa fileira so faria "Prontos" parecer um genero de historia.

            A ajuda embaixo muda com a escolha porque a distincao nao e obvia de nome: "ainda
            mudando" precisa dizer que o balao de fala pode estar diferente amanha — que e
            literalmente o exemplo que ele deu. */}
        <div className="biblioteca-exibicoes" role="group" aria-label="Mostrar">
          {EXIBICOES.map((e) => (
            <button
              key={e}
              type="button"
              className={`biblioteca-exibicao ${exibicao === e ? "ativo" : ""}`}
              aria-pressed={exibicao === e}
              onClick={() => setExibicao(e)}
            >
              {ROTULO_EXIBICAO[e]}
            </button>
          ))}
        </div>
        <p className="biblioteca-exibicao-ajuda">{AJUDA_EXIBICAO[exibicao]}</p>

        <div className="biblioteca-filtros" role="group" aria-label="Filtrar por categoria">
          {filtros.map((f) => (
            <button
              key={f.id}
              type="button"
              className={`biblioteca-filtro ${filtro === f.id ? "ativo" : ""}`}
              aria-pressed={filtro === f.id}
              onClick={() => setFiltro(f.id)}
            >
              {f.nome}
            </button>
          ))}
        </div>

        {erro && <p className="biblioteca-aviso erro">{erro}</p>}

        {!docs && !erro && <p className="biblioteca-aviso">Carregando as histórias…</p>}

        {docs && series.length === 0 && (() => {
          // T8.23 — QUEM DECIDE A FRASE É `estado-vazio.ts`, e não uma pilha de ternários aqui.
          // O defeito medido em produção era um <h2> afirmando sobre a PRATELEIRA enquanto o <p>
          // logo abaixo afirmava sobre o FILTRO. `docs` é a lista publicada ANTES do filtro, e é
          // ela que separa os dois mundos.
          const vazia = estadoVazio({
            quantosPublicados: docs.length,
            busca,
            filtro,
            exibicao,
            logado: !!globalState?.appUser,
          });
          return (
            <div className="biblioteca-vazia">
              <h2>{vazia.titulo}</h2>
              <p>{vazia.explicacao}</p>
              {vazia.acao && (
                <button
                  type="button"
                  className="biblioteca-botao"
                  onClick={() => {
                    // Quando a saída é tirar o filtro, ela LIMPA o estado desta tela — navegar
                    // para /biblioteca estando em /biblioteca não faria nada visível.
                    if (vazia.limparFiltro) {
                      setBusca("");
                      setFiltro("todas");
                      setExibicao("todos");
                      return;
                    }
                    navigate(vazia.acao!.destino);
                  }}
                >
                  {vazia.acao.rotulo}
                </button>
              )}
            </div>
          );
        })()}

        {series.map((serie) => (
          <section key={serie.id} className="biblioteca-serie">
            <div className="biblioteca-serie-cabecalho">
              <div>
                <span className="biblioteca-etiqueta">{ROTULO_CATEGORIA[serie.categoria]}</span>
                <h2>{serie.titulo}</h2>
                <p className="biblioteca-serie-meta">
                  {serie.capitulos.length === 1
                    ? "história avulsa"
                    : `${serie.capitulos.length} capítulos`}
                  {serie.capitulos[0]?.autor?.nome ? ` · ${serie.capitulos[0].autor.nome}` : ""}
                </p>
              </div>
            </div>

            <ol className="biblioteca-capitulos">
              {serie.capitulos.map((cap, i) => (
                <li key={cap.id}>
                  <button
                    type="button"
                    className="biblioteca-capitulo"
                    onClick={() => navigate(`/biblioteca/${cap.id}`)}
                  >
                    <span className="biblioteca-capitulo-numero">
                      {String(cap.publicacao?.serie?.capitulo ?? i + 1).padStart(2, "0")}
                    </span>
                    <span className="biblioteca-capitulo-texto">
                      <span className="biblioteca-capitulo-titulo">{cap.titulo}</span>
                      <span className="biblioteca-capitulo-meta">
                        {rotuloDePaginas(paginasDaFicha(cap)) ?? "tamanho não informado"}
                        {cap.publicacao?.serie?.resumo ? ` · ${cap.publicacao.serie.resumo}` : ""}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ol>
          </section>
        ))}
      </main>
    </div>
  );
}

export default Biblioteca;
